import { Menu, Transition } from '@headlessui/react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import SearchField from '../../Market/SearchField/SearchField';
import ActionBtn from './ActionBtn';
import { cn } from '../../../../utils';
import { filterLoveFromCommunityCategories } from '../../../../utils/community';
import {
  FOR_YOU_MARKET_ROUTE,
  FOR_YOU_RONS_TIPS_ROUTE,
} from '../../../../utils/routes';

const MenuContainer = ({
  label,
  subMenus,
  link,
  isHighlighted,
  dropdownStyle,
}) => {
  const lang = useSelector(({ common }) => common?.language);
  const ref = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const router = useRouter();
  const { city = 'zurich', tag_filter = 'most_popular' } = router.query;

  const handleMenuOpen = () => {
    setIsOpen(true);
  };

  const handleMenuClose = () => {
    setIsOpen(false);
  };

  const getQuery = (categoryId) => {
    if (categoryId == 15 || categoryId == 16) {
      // 15 = Love, 16 = My Area
      return {
        city: !city ? 'zurich' : city,
      };
    } else {
      return {
        city: !city ? 'zurich' : city,
        tag_filter,
      };
    }
  };

  return (
    <Menu as='div' className='relative inline-block text-left'>
      {({ open }) => (
        <>
          <Menu.Button
            ref={ref}
            className={`flex cursor-pointer items-center rounded-none text-black focus:outline-none`}
            onMouseEnter={handleMenuOpen}
            onMouseLeave={handleMenuClose}
          >
            <Link
              href={link}
              className={cn(
                `text-[16px] font-semibold uppercase text-primary  md:text-[16px]`,
                isHighlighted &&
                  ' underline decoration-primary decoration-[2px] underline-offset-4 lg:underline-offset-8'
              )}
            >
              {label}
            </Link>
          </Menu.Button>

          <Transition
            show={open || isOpen}
            as={Fragment}
            enter='transition ease-out duration-100'
            enterFrom='transform opacity-0 scale-95'
            enterTo='transform opacity-100 scale-100'
            leave='transition ease-in duration-75'
            leaveFrom='transform opacity-100 scale-100'
            leaveTo='transform opacity-0 scale-95'
          >
            <Menu.Items
              static
              className={cn(
                'absolute left-0 !z-[10000] flex min-w-[180px] max-w-none origin-top-left flex-col !bg-white shadow-lg outline-none',
                dropdownStyle
              )}
              ref={ref}
              onMouseEnter={handleMenuOpen}
              onMouseLeave={handleMenuClose}
            >
              {subMenus?.map((item, index) => (
                <Menu.Item key={index}>
                  {({ active }) => (
                    <Link
                      href={
                        item?.url?.includes('rons-tips') ||
                        item?.url?.includes('market') ||
                        item?.url?.includes('/community/for-you')
                          ? item.url
                          : {
                              pathname: item.url,
                              query: getQuery(item?.id),
                            }
                      }
                      className={`${
                        active ? 'bg-gray-100' : ''
                      } w-full cursor-pointer px-3 py-2 font-semibold uppercase hover:bg-gray-100`}
                    >
                      {item.name === 'for_you'
                        ? lang?.common?.for_you
                        : item.name}
                    </Link>
                  )}
                </Menu.Item>
              ))}
            </Menu.Items>
          </Transition>
        </>
      )}
    </Menu>
  );
};

export default function SubNavBar({ isAuthenticated }) {
  const router = useRouter();
  const { tag_filter = 'most_popular', city } = router?.query;
  const [cachedCategories, setCachedCategories] = useState([]);
  const categories = useSelector(({ community }) => {
    return community?.categories;
  });

  useEffect(() => {
    if (categories === null) {
      setCachedCategories(JSON.parse(localStorage.getItem('categories')));
    }
  }, []);

  const lang = useSelector(({ common }) => common?.language);

  const ronTipsSubmenus = [
    {
      name: lang?.rons_tips?.guides,
      url: `/rons-tips/guides?city=${city}&tag_filter=${tag_filter}`,
    },
    {
      name: lang?.rons_tips?.food_and_shopping,
      url: `/rons-tips/food-shopping?city=${city}&tag_filter=${tag_filter}`,
    },
    {
      name: lang?.rons_tips?.familylife || 'Family Life',
      url: `/rons-tips/family-life?city=${city}&tag_filter=${tag_filter}`,
    },
    {
      name: lang?.rons_tips?.people,
      url: `/rons-tips/people?city=${city}&tag_filter=${tag_filter}`,
    },
    {
      name: lang?.rons_tips?.out_of_town,
      url: `/rons-tips/out-of-town?city=${city}&tag_filter=${tag_filter}`,
    },
    {
      name: lang?.rons_tips?.contest,
      url: `/rons-tips/contest?city=${city}&tag_filter=${tag_filter}`,
    },
    {
      name: lang?.market?.events,
      url: `/market/events?city=${city}&tag_filter=${tag_filter}`,
    },
    {
      name: lang?.rons_tips?.newsletter_archive,
      url: `/rons-tips/newsletter-archive?city=${city}`,
    },
    isAuthenticated && {
      name: lang?.common?.for_you,
      url: FOR_YOU_RONS_TIPS_ROUTE(city),
    },
  ];

  const marketSubmenus = [
    {
      name: lang?.market?.events,
      url: `/market/events?city=${city}&tag_filter=${tag_filter}`,
    },
    {
      name: lang?.market?.housing,
      url: `/market/housing?city=${city}&tag_filter=${tag_filter}`,
    },
    {
      name: lang?.market?.second_hand,
      url: `/market/secondhand?city=${city}&tag_filter=${tag_filter}`,
    },
    {
      name: lang?.market?.jobs,
      url: `/market/jobs?city=${city}&tag_filter=${tag_filter}`,
    },
    {
      name: lang?.market?.city_offer,
      url: `/market/cityoffer?city=${city}&tag_filter=${tag_filter}`,
    },
    isAuthenticated && {
      name: lang?.common?.for_you,
      url: FOR_YOU_MARKET_ROUTE(city),
    },
  ];
  const showSearch =
    (router?.pathname?.includes('/rons-tips') &&
      !router?.pathname?.includes('/rons-tips/search')) ||
    (router?.pathname?.includes('/community') &&
      !router?.pathname?.includes('/community/search') &&
      !router?.pathname?.includes('/community/start-discussion'));

  return (
    <nav className='border-b-[1px] border-border-mid bg-white md:flex md:justify-center'>
      <div className='custom-container flex w-full items-center  justify-between py-[12px]'>
        <div className='flex h-[44px] min-w-full items-center justify-between gap-x-[12px] md:min-w-0 md:gap-x-[24px] xl:gap-x-[83px]'>
          <MenuContainer
            label={lang?.common?.rons_tips || 'Tips for more city life'}
            subMenus={ronTipsSubmenus}
            link={`/rons-tips?city=${city}&tag_filter=${tag_filter}`}
            isHighlighted={router?.pathname?.includes('/rons-tips')}
          />
          <MenuContainer
            label={lang?.common?.ron_community || 'Community'}
            subMenus={
              filterLoveFromCommunityCategories(categories) ||
              filterLoveFromCommunityCategories(cachedCategories)
            }
            link={`/community?city=${city}&tag_filter=${tag_filter}`}
            isHighlighted={router?.pathname?.includes('/community')}
          />
          <MenuContainer
            label={lang?.common?.ron_market || 'Market'}
            subMenus={marketSubmenus}
            link={`/market?city=${city}&tag_filter=${tag_filter}`}
            isHighlighted={router.pathname.includes('/market')}
            dropdownStyle='left-[-67px] md:left-0'
          />
        </div>

        <div className='hidden min-w-[630px] items-center justify-end gap-2 md:flex'>
          {showSearch && (
            <SearchField
              withoutPostType={true}
              isRounded={true}
              className='mb-6 w-full '
            />
          )}
          {!router?.pathname?.includes('/rons-tips') ? <ActionBtn /> : null}
        </div>
      </div>
    </nav>
  );
}
